import React, { useEffect, useRef } from 'react';
import { Card, Button, FormCheck } from 'react-bootstrap';
import { Formik, Form, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isNull, isUndefined } from 'lodash';
import { selectIsFrontline, selectIsInsurer } from '@components/Auth';
import { BackButton, FormGroupControl, Loading, NoData, LoadingError } from '@components/Base';
import { ServerError } from '@components/Helper';
import { selectHealthPolicyClaim } from './selector';
import { useInitialValues, useFormValuesSchema } from './hook';
import { policyFetch, healthClaimCreate, HealthClaimCreatePayload, insurancePolicyUserFetch } from './action';
import { FormValues } from './schema';
import { PolicyOverviewCardBody } from './policy-overview-card-body';
import { PaymentInfoCardBody } from './payment-info-card-body';
import { ClaimCardBody } from './claim-card-body';
import { ContactInfoCardBody } from './contact-info-card-body';

const HealthPolicyClaim = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isFrontline = useSelector(selectIsFrontline);
  const isInsurer = useSelector(selectIsInsurer);
  const shouldRenderEmailNotificationsSwtich = isFrontline || isInsurer;
  const formikRef = useRef<FormikProps<FormValues>>(null);

  const { policy, insurancePolicyUser, serverErrors } = useSelector(selectHealthPolicyClaim);
  const { insurancePolicyId, insurancePolicyUserId } = useParams();

  const isPolicyLoaded = !isUndefined(policy);
  const isPolicyError = isNull(policy);
  const hasPolicy = isPolicyLoaded && !isNull(policy);

  const isPolicyUserLoaded = !isUndefined(insurancePolicyUser);
  const isPolicyUserError = isNull(insurancePolicyUser);
  const hasPolicyUser = isPolicyUserLoaded && !isNull(insurancePolicyUser);

  const initialValues = useInitialValues();
  const validationSchema = useFormValuesSchema();

  const handleSubmit = (values: FormValues) => {
    const payload: HealthClaimCreatePayload = {
      ...values,
      insurancePolicyUserId: Number(insurancePolicyUser?.id),
      coveredByOtherInsurance: values.coveredByOtherInsurance === 'true',
      businessPartnerId: Number(values.businessPartnerId),
      roomStay: values.roomStay === 'true',
    };

    dispatch(healthClaimCreate(payload));
  };

  useEffect(() => {
    dispatch(policyFetch({ id: Number(insurancePolicyId) }));
  }, [insurancePolicyId]);

  useEffect(() => {
    dispatch(insurancePolicyUserFetch({ id: Number(insurancePolicyUserId) }));
  }, [insurancePolicyUserId]);

  useEffect(() => {
    if (serverErrors && Object.keys(serverErrors).length > 0) {
      formikRef.current?.setSubmitting(false);
    }
  }, [serverErrors]);

  return (
    <>
      <BackButton className="mb-4" />
      {!isPolicyLoaded && !isPolicyUserLoaded && <Loading />}
      {(isPolicyError || isPolicyUserError) && <LoadingError />}
      {((isPolicyLoaded && !hasPolicy) || (isPolicyUserLoaded && !hasPolicyUser)) && <NoData />}

      {hasPolicy && hasPolicyUser && (
        <Card>
          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, isSubmitting, handleChange }) => (
              <Form>
                <Card.Header className="fs-5 fw-bold">{t('common:claimForm')}</Card.Header>

                <PolicyOverviewCardBody />
                <ContactInfoCardBody />
                <ClaimCardBody insurancePolicyUser={insurancePolicyUser} />
                <PaymentInfoCardBody />

                {shouldRenderEmailNotificationsSwtich && (
                  <Card.Body>
                    <FormCheck
                      type="switch"
                      bsPrefix="form-check"
                      bsSwitchPrefix="form-switch"
                      name="emailNotifications"
                      label="Email notifications"
                      inline
                      checked={values.emailNotifications}
                      onChange={handleChange}
                    />
                  </Card.Body>
                )}

                <Card.Body style={{ whiteSpace: 'pre-line' }}>
                  <FormGroupControl
                    type="checkbox"
                    name="acceptTermsAndConditions"
                    label={t('healthInsuranceClaimForm.acceptTermsAndConditions.label')}
                    bsPrefix="form-check-input"
                    groupProps={{ className: 'form-check form-check-inline' }}
                  />
                </Card.Body>

                <Card.Footer className="bg-white">
                  <div className="text-center my-4">
                    <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
                      {t('common:submit')}
                    </Button>
                  </div>
                </Card.Footer>

                <ServerError errors={serverErrors} />
              </Form>
            )}
          </Formik>
        </Card>
      )}
    </>
  );
};

export { HealthPolicyClaim };
